import React, { useState, useEffect } from 'react';
import { User, isCurrentUserSiteAdmin } from './UserManagementPage';
import { useOri } from '../../context/OriContext';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Switch, FormControlLabel, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box } from '@mui/material';

interface UserFormModalProps {
    user: User | null;
    onClose: () => void;
    onSave: (user: User & { password?: string }) => void;
    error?: string | null;
}

const UserFormModal: React.FC<UserFormModalProps> = ({ user, onClose, onSave, error }) => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        agencyOri: '',
        password: '',
        isOrgAdmin: false,
        isSiteAdmin: false,
    });

    const isNewUser = !user;
    const isEditingUserSiteAdmin = isCurrentUserSiteAdmin();
    const { availableOris, selectedOri } = useOri();

    useEffect(() => {
        if (user) {
            setFormData({
                username: user.username || '',
                email: user.attributes.email || '',
                firstName: user.attributes.given_name || '',
                lastName: user.attributes.family_name || '',
                agencyOri: user.attributes['custom:agency_ori'] || '',
                password: '',
                isOrgAdmin: user.groups?.includes('org-admin') || false,
                isSiteAdmin: user.groups?.includes('site-admin') || false,
            });
        } else {
            // Reset form for new user
            setFormData({
                username: '',
                email: '',
                firstName: '',
                lastName: '',
                agencyOri: selectedOri || (availableOris.length > 0 ? availableOris[0].ori : ''),
                password: '',
                isOrgAdmin: false,
                isSiteAdmin: false,
            });
        }
    }, [user, availableOris, selectedOri]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSelectChange = (e: SelectChangeEvent) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (user && formData.email !== user.attributes.email) {
            if (!window.confirm('Changing the email address will require the user to re-verify their email. Do you want to continue?')) {
                return;
            }
        }

        const newGroups: string[] = [];
        if (formData.isOrgAdmin) newGroups.push('org-admin');
        if (formData.isSiteAdmin && isEditingUserSiteAdmin) newGroups.push('site-admin');

        const finalUser: User & { password?: string } = {
            username: formData.username,
            attributes: {
                ...(user?.attributes || {}),
                email: formData.email,
                given_name: formData.firstName,
                family_name: formData.lastName,
                'custom:agency_ori': formData.agencyOri,
            },
            groups: newGroups,
        };

        if (isNewUser && formData.password) {
            finalUser.password = formData.password;
        }
        onSave(finalUser);
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isNewUser ? 'Add New User' : 'Edit User'}</DialogTitle>
            <form onSubmit={handleSubmit} autoComplete="off" id="user-form">
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        name="username"
                        label="Username"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={formData.username}
                        onChange={handleInputChange}
                        required
                        disabled={!!user}
                        autoComplete="off"
                    />
                    <TextField
                        margin="dense"
                        name="email"
                        label="Email"
                        type="email"
                        fullWidth
                        variant="standard"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        autoComplete="off"
                    />
                    <TextField
                        margin="dense"
                        name="firstName"
                        label="First Name"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={formData.firstName}
                        onChange={handleInputChange}
                    />
                    <TextField
                        margin="dense"
                        name="lastName"
                        label="Last Name"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={formData.lastName}
                        onChange={handleInputChange}
                    />
                    <FormControl fullWidth variant="standard" sx={{ mt: 2 }}>
                        <InputLabel id="agency-ori-select-label">Agency ORI</InputLabel>
                        <Select
                            labelId="agency-ori-select-label"
                            name="agencyOri"
                            value={formData.agencyOri}
                            onChange={handleSelectChange}
                            disabled={!isEditingUserSiteAdmin}
                            required
                        >
                            {availableOris.map(agency => (
                                <MenuItem key={agency.ori} value={agency.ori}>{agency.name} ({agency.ori})</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isNewUser && (
                        <TextField
                            margin="dense"
                            name="password"
                            label="Password"
                            type="password"
                            fullWidth
                            variant="standard"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            autoComplete="new-password"
                        />
                    )}
                    <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isOrgAdmin}
                                    onChange={handleInputChange}
                                    name="isOrgAdmin"
                                />
                            }
                            label="Organization Admin"
                        />
                        {isEditingUserSiteAdmin && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isSiteAdmin}
                                        onChange={handleInputChange}
                                        name="isSiteAdmin"
                                    />
                                }
                                label="Site Admin"
                            />
                        )}
                    </Box>
                    {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="submit" form="user-form">Save</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default UserFormModal;
